import { Badge, Button, Card, LoadingContainer, Modal } from '@/components/ui';
import { useDriveAudioManagement, useEmpresasSysadmin } from '@/hooks/useSysadmin';
import { sysadminService, type CarpetaDriveDto, type DriveFileItemDto } from '@/services/sysadmin.service';
import {
  AlertCircle,
  AlertTriangle,
  Check,
  CheckCircle2,
  Copy,
  Edit3,
  ExternalLink,
  FileAudio,
  FolderOpen,
  FolderSearch,
  Pause,
  Play,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
} from 'lucide-react';
import React, { useCallback, useMemo, useRef, useState } from 'react';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export function GestionDriveAudiosPanel() {
  const { empresas, isLoading: loadingEmpresas } = useEmpresasSysadmin();
  const {
    files,
    duplicateGroups,
    totalDuplicates,
    isLoading,
    isProcessing,
    error,
    cargarArchivos,
    renombrarArchivo,
    autoNormalizar,
    detectarDuplicados,
    eliminarArchivo,
    eliminarLote,
  } = useDriveAudioManagement();

  const [selectedNit, setSelectedNit] = useState<string>('');
  const [folderId, setFolderId] = useState<string>('');
  const [carpetasEmpresa, setCarpetasEmpresa] = useState<CarpetaDriveDto[]>([]);
  const [loadingCarpetas, setLoadingCarpetas] = useState(false);
  const [activeView, setActiveView] = useState<'list' | 'duplicates'>('list');

  // Audio Player State
  const [playingFileId, setPlayingFileId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // Modal: Renombrar individual
  const [fileToRename, setFileToRename] = useState<DriveFileItemDto | null>(null);
  const [customNewName, setCustomNewName] = useState<string>('');

  // Modal: Eliminar individual
  const [fileToDelete, setFileToDelete] = useState<DriveFileItemDto | null>(null);

  // Modal: Auto-normalizar
  const [showNormalizeModal, setShowNormalizeModal] = useState(false);
  const [normalizePlan, setNormalizePlan] = useState<Array<{ fileId: string; currentName: string; targetName: string; status: string }>>(
    []
  );

  // Modal: Duplicados
  const [showDuplicatesModal, setShowDuplicatesModal] = useState(false);
  const [selectedDuplicatesToDelete, setSelectedDuplicatesToDelete] = useState<Set<string>>(new Set());

  // Filtro: categorizar/filtrar audios por asesoría (requiere haber cargado la carpeta con
  // empresa seleccionada — el cruce con Audio.asesoriaId solo viene si se pidió con `nit`).
  const [selectedAsesoriaFilter, setSelectedAsesoriaFilter] = useState<string>('all');

  // Mensajes de éxito / feedback
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Cargar carpeta directamente
  const ejecutarCarga = useCallback(
    async (idToLoad: string, nit?: string) => {
      if (!idToLoad.trim()) return;
      setSuccessMessage(null);
      const empresaNit = nit ?? selectedNit ?? undefined;
      await cargarArchivos(idToLoad.trim(), empresaNit);
      await detectarDuplicados(idToLoad.trim(), empresaNit);
    },
    [cargarArchivos, detectarDuplicados, selectedNit]
  );

  // Manejar selección automática de empresa
  const handleEmpresaChange = async (nit: string) => {
    setSelectedNit(nit);
    setCarpetasEmpresa([]);
    if (!nit) {
      setFolderId('');
      return;
    }

    const emp = empresas.find((e) => e.nitId === nit);
    let resolvedFolderId = emp?.carpetaId || '';

    setLoadingCarpetas(true);
    try {
      const res = await sysadminService.listarCarpetasEmpresaDrive(nit);
      if (res.success && res.carpetas && res.carpetas.length > 0) {
        setCarpetasEmpresa(res.carpetas);

        // Buscar si existe una carpeta específica de audios/grabaciones
        const audioFolder = res.carpetas.find((c) => /audio|grabaci|asesor/i.test(c.name) || /audio|grabaci/i.test(c.path));

        if (audioFolder) {
          resolvedFolderId = audioFolder.id;
        } else if (!resolvedFolderId) {
          resolvedFolderId = res.carpetas[0].id;
        }
      }
    } catch (err) {
      console.warn('No se pudieron listar subcarpetas de Drive para la empresa:', err);
    } finally {
      setLoadingCarpetas(false);
    }

    if (resolvedFolderId) {
      setFolderId(resolvedFolderId);
      await ejecutarCarga(resolvedFolderId, nit);
    }
  };

  const handleSubcarpetaChange = async (subFolderId: string) => {
    if (!subFolderId) return;
    setFolderId(subFolderId);
    await ejecutarCarga(subFolderId);
  };

  const handleCargarCarpeta = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!folderId.trim()) return;
    await ejecutarCarga(folderId.trim());
  };

  // Reproductor de Audio
  const togglePlay = (file: DriveFileItemDto) => {
    if (playingFileId === file.id && isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      setPlayingFileId(file.id);
      setIsPlaying(true);
      if (audioRef.current) {
        audioRef.current.src = `${API_BASE_URL}/audio/stream/${file.id}`;
        audioRef.current.play().catch((err) => {
          console.error('Error al reproducir audio:', err);
          setIsPlaying(false);
        });
      }
    }
  };

  // Filtrar archivos de audio
  const audioFiles = useMemo(() => {
    return files.filter(
      (f) => f.mimeType?.startsWith('audio/') || f.name.endsWith('.mp3') || f.name.endsWith('.wav') || f.name.endsWith('.m4a')
    );
  }, [files]);

  // Set de IDs duplicados conocidos
  const duplicateFileIds = useMemo(() => {
    const ids = new Set<string>();
    for (const group of duplicateGroups) {
      for (const f of group.files) {
        if (!f.isOriginal) {
          ids.add(f.id);
        }
      }
    }
    return ids;
  }, [duplicateGroups]);

  // Asesorías presentes en la carpeta cargada (solo se conocen si se cargó con empresa
  // seleccionada, ya que el backend cruza cada archivo con Audio.asesoriaId por ese nit).
  const asesoriasDisponibles = useMemo(() => {
    const porId = new Map<number, string>();
    for (const file of audioFiles) {
      if (file.asesoriaId != null) {
        porId.set(file.asesoriaId, file.asesoriaLabel || `Asesoría ${file.asesoriaId}`);
      }
    }
    return Array.from(porId.entries())
      .map(([asesoriaId, label]) => ({ asesoriaId, label }))
      .sort((a, b) => a.asesoriaId - b.asesoriaId);
  }, [audioFiles]);

  const audioFilesFiltrados = useMemo(() => {
    if (selectedAsesoriaFilter === 'all') return audioFiles;
    if (selectedAsesoriaFilter === 'sin-asesoria') return audioFiles.filter((f) => f.asesoriaId == null);
    const id = Number(selectedAsesoriaFilter);
    return audioFiles.filter((f) => f.asesoriaId === id);
  }, [audioFiles, selectedAsesoriaFilter]);

  // Abrir Modal de Renombrar
  const handleOpenRename = (file: DriveFileItemDto) => {
    setFileToRename(file);
    setCustomNewName(file.suggestedName || file.name);
  };

  const handleConfirmRename = async () => {
    if (!fileToRename || !customNewName.trim()) return;
    try {
      await renombrarArchivo(fileToRename.id, customNewName.trim());
      setSuccessMessage(`Archivo renombrado exitosamente a "${customNewName.trim()}"`);
      setFileToRename(null);
    } catch (err: any) {
      alert(err.message || 'Error al renombrar');
    }
  };

  // Abrir Modal de Auto-Normalización
  const handleOpenNormalize = async () => {
    if (!folderId) return;
    try {
      const res = await autoNormalizar(folderId, false);
      if (res.success) {
        setNormalizePlan(res.items || []);
        setShowNormalizeModal(true);
      }
    } catch (err: any) {
      alert(err.message || 'Error calculando normalización');
    }
  };

  const handleApplyNormalize = async () => {
    if (!folderId) return;
    try {
      await autoNormalizar(folderId, true);
      setSuccessMessage('Normalización aplicada con éxito a todos los archivos de la carpeta.');
      setShowNormalizeModal(false);
    } catch (err: any) {
      alert(err.message || 'Error aplicando normalización');
    }
  };

  // Abrir Modal de Duplicados
  const handleOpenDuplicates = async () => {
    if (!folderId) return;
    try {
      const res = await detectarDuplicados(folderId, selectedNit || undefined);
      if (res.success) {
        const toDelete = new Set<string>();
        for (const grp of res.groups) {
          for (const f of grp.files) {
            if (!f.isOriginal) {
              toDelete.add(f.id);
            }
          }
        }
        setSelectedDuplicatesToDelete(toDelete);
        setShowDuplicatesModal(true);
      }
    } catch (err: any) {
      alert(err.message || 'Error detectando duplicados');
    }
  };

  const toggleSelectDuplicate = (id: string) => {
    setSelectedDuplicatesToDelete((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleConfirmDeleteDuplicates = async () => {
    if (selectedDuplicatesToDelete.size === 0) return;
    const count = selectedDuplicatesToDelete.size;
    if (!window.confirm(`¿Estás seguro de que deseas enviar ${count} archivos duplicados a la papelera de Google Drive (reversible)?`)) {
      return;
    }
    try {
      await eliminarLote(Array.from(selectedDuplicatesToDelete), folderId, selectedNit || undefined);
      setSuccessMessage(`Se eliminaron ${count} archivos duplicados correctamente.`);
      setShowDuplicatesModal(false);
      setSelectedDuplicatesToDelete(new Set());
    } catch (err: any) {
      alert(err.message || 'Error al eliminar duplicados');
    }
  };

  const handleConfirmDeleteSingle = async () => {
    if (!fileToDelete) return;
    try {
      await eliminarArchivo(fileToDelete.id, folderId, selectedNit || undefined);
      setSuccessMessage(`Archivo "${fileToDelete.name}" eliminado de Google Drive.`);
      setFileToDelete(null);
    } catch (err: any) {
      alert(err.message || 'Error al eliminar archivo');
    }
  };

  const formatFileSize = (bytes?: number) => {
    if (!bytes) return 'N/A';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Audio element oculto para streaming */}
      <audio ref={audioRef} onEnded={() => setIsPlaying(false)} onError={() => setIsPlaying(false)} className="hidden" />

      {/* Tarjeta de Búsqueda y Selección de Carpeta */}
      <Card className="p-6 bg-white border border-gray-200 shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2">
              <FolderSearch className="w-5 h-5 text-(--admin-blue)" />
              Explorador y Normalizador de Audios en Google Drive
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              Inspecciona archivos de audio en Google Drive, reproduce grabaciones, estandariza nombres a formato corto y elimina duplicados
              con confirmación.
            </p>
          </div>
        </div>

        {error && (
          <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-lg flex items-start gap-3 border border-red-200">
            <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold">Error</p>
              <p className="text-sm">{error}</p>
            </div>
          </div>
        )}

        {successMessage && (
          <div className="mb-4 p-4 bg-emerald-50 text-emerald-800 rounded-lg flex items-start justify-between gap-3 border border-emerald-200">
            <div className="flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0" />
              <p className="text-sm font-medium">{successMessage}</p>
            </div>
            <button onClick={() => setSuccessMessage(null)} className="text-emerald-600 hover:text-emerald-800">
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        <form onSubmit={handleCargarCarpeta} className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Seleccionar Empresa</label>
              <select
                value={selectedNit}
                onChange={(e) => handleEmpresaChange(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-(--admin-blue) focus:outline-hidden bg-white text-sm"
                disabled={loadingEmpresas}
              >
                <option value="">-- Seleccionar empresa para auto-cargar --</option>
                {empresas.map((emp) => (
                  <option key={emp.nitId} value={emp.nitId}>
                    {emp.nombre} ({emp.nitId})
                  </option>
                ))}
              </select>
            </div>

            {carpetasEmpresa.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1 items-center justify-between">
                  <span>Subcarpeta de Drive</span>
                  {loadingCarpetas && <span className="text-xs text-blue-600 animate-pulse">Explorando...</span>}
                </label>
                <select
                  value={folderId}
                  onChange={(e) => handleSubcarpetaChange(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-(--admin-blue) focus:outline-hidden bg-white text-sm"
                >
                  {carpetasEmpresa.map((c) => (
                    <option key={c.id} value={c.id}>
                      📁 {c.path || c.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className={carpetasEmpresa.length > 0 ? 'md:col-span-1' : 'md:col-span-1'}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                ID de Carpeta en Google Drive <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={folderId}
                onChange={(e) => setFolderId(e.target.value)}
                placeholder="Ej: 1tQANCgKYOnb_ux4yYsEBqnKs4bOj2t11"
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-(--admin-blue) focus:outline-hidden text-sm font-mono"
              />
            </div>

            <div className="flex gap-2">
              <Button
                type="submit"
                disabled={isLoading || isProcessing || !folderId.trim()}
                className="flex-1 bg-(--admin-blue) hover:bg-blue-700 text-white flex items-center justify-center gap-2 py-2"
              >
                <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
                {isLoading ? 'Cargando...' : 'Cargar Audios'}
              </Button>
            </div>
          </div>
        </form>
      </Card>

      {/* Barra de Acciones y Estadísticas */}
      {folderId && files.length > 0 && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center justify-between gap-4 bg-gray-50 p-4 rounded-xl border border-gray-200">
            <div className="flex flex-wrap items-center gap-3">
              <Badge variant="gray" className="px-3 py-1 text-sm bg-white border border-gray-200">
                <FileAudio className="w-4 h-4 mr-1.5 text-blue-600" />
                Total Audios: <strong className="ml-1">{audioFiles.length}</strong>
              </Badge>

              {totalDuplicates > 0 ? (
                <button
                  type="button"
                  onClick={() => setActiveView('duplicates')}
                  className="cursor-pointer transition-transform hover:scale-105"
                >
                  <Badge variant="warning" className="px-3 py-1 text-sm bg-amber-100 text-amber-900 border border-amber-300 shadow-xs">
                    <AlertTriangle className="w-4 h-4 mr-1.5 text-amber-600" />
                    Duplicados detectados:{' '}
                    <strong className="ml-1">
                      {totalDuplicates} (en {duplicateGroups.length} grupos)
                    </strong>
                  </Badge>
                </button>
              ) : (
                <Badge variant="green" className="px-3 py-1 text-sm bg-emerald-50 text-emerald-800 border border-emerald-300">
                  <CheckCircle2 className="w-4 h-4 mr-1.5 text-emerald-600" />
                  Sin duplicados
                </Badge>
              )}
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                onClick={handleOpenNormalize}
                disabled={isProcessing}
                variant="outline"
                className="border-(--admin-blue) text-(--admin-blue) hover:bg-blue-50 flex items-center gap-1.5 text-sm"
              >
                <Sparkles className="w-4 h-4" />
                Auto-Normalizar Nombres
              </Button>

              {totalDuplicates > 0 && (
                <Button
                  onClick={handleOpenDuplicates}
                  disabled={isProcessing}
                  className="bg-amber-600 hover:bg-amber-700 text-white flex items-center gap-1.5 text-sm"
                >
                  <Copy className="w-4 h-4" />
                  Limpieza Rápida ({totalDuplicates})
                </Button>
              )}
            </div>
          </div>

          {/* Selector de Vista: Todos vs Grupos de Duplicados */}
          <div className="flex items-center gap-3 border-b border-gray-200 pb-2">
            <button
              onClick={() => setActiveView('list')}
              className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                activeView === 'list' ? 'bg-blue-50 text-blue-700 border border-blue-200 shadow-xs' : 'text-gray-600 hover:bg-gray-100'
              }`}
            >
              <FileAudio className="w-4 h-4" />
              Explorador de Audios ({audioFiles.length})
            </button>

            {totalDuplicates > 0 && (
              <button
                onClick={() => setActiveView('duplicates')}
                className={`px-4 py-2 text-sm font-semibold rounded-lg transition-colors flex items-center gap-2 ${
                  activeView === 'duplicates'
                    ? 'bg-amber-50 text-amber-900 border border-amber-300 shadow-xs'
                    : 'text-gray-600 hover:bg-amber-50/50 hover:text-amber-800'
                }`}
              >
                <Copy className="w-4 h-4 text-amber-600" />
                Grupos de Duplicados ({duplicateGroups.length} grupos • {totalDuplicates} clones)
              </button>
            )}

            {activeView === 'list' && asesoriasDisponibles.length > 0 && (
              <div className="flex items-center gap-2 ml-auto">
                <label className="text-xs font-medium text-gray-500">Categorizar por asesoría:</label>
                <select
                  value={selectedAsesoriaFilter}
                  onChange={(e) => setSelectedAsesoriaFilter(e.target.value)}
                  className="px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg bg-white focus:ring-2 focus:ring-(--admin-blue) focus:outline-hidden"
                >
                  <option value="all">Todas las asesorías ({audioFiles.length})</option>
                  {asesoriasDisponibles.map(({ asesoriaId, label }) => (
                    <option key={asesoriaId} value={asesoriaId}>
                      {label}
                    </option>
                  ))}
                  <option value="sin-asesoria">Sin asesoría identificada</option>
                </select>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Contenido Principal según Vista */}
      {isLoading ? (
        <LoadingContainer text="Consultando archivos en Google Drive..." />
      ) : activeView === 'duplicates' && duplicateGroups.length > 0 ? (
        /* VISTA VISUAL DE GRUPOS DE DUPLICADOS */
        <div className="space-y-6">
          <div className="p-4 bg-amber-50/80 border border-amber-200 rounded-xl flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="font-bold text-amber-900 text-sm">
                  Detección Visual de Archivos Duplicados ({duplicateGroups.length} Grupos / {totalDuplicates} Clones)
                </h3>
                <p className="text-xs text-amber-800 mt-0.5">
                  Cada grupo contiene copias exactas del mismo audio. El archivo marcado en{' '}
                  <strong className="text-emerald-800">verde (ORIGINAL)</strong> se mantendrá guardado.
                </p>
              </div>
            </div>

            {selectedDuplicatesToDelete.size > 0 && (
              <Button
                onClick={handleConfirmDeleteDuplicates}
                disabled={isProcessing}
                className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-2 text-sm py-2 px-4 shadow-sm shrink-0"
              >
                <Trash2 className="w-4 h-4" />
                Eliminar {selectedDuplicatesToDelete.size} Duplicados Seleccionados
              </Button>
            )}
          </div>

          <div className="grid grid-cols-1 gap-6">
            {duplicateGroups.map((group, grpIdx) => {
              const originalFile = group.files.find((f) => f.isOriginal);
              const cloneFiles = group.files.filter((f) => !f.isOriginal);

              return (
                <div key={group.key || grpIdx} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
                  {/* Encabezado del Grupo */}
                  <div className="bg-gray-50/90 px-5 py-3.5 border-b border-gray-200 flex flex-wrap items-center justify-between gap-3">
                    <div className="flex items-center gap-3">
                      <span className="w-7 h-7 rounded-lg bg-amber-100 text-amber-800 font-bold text-xs flex items-center justify-center border border-amber-200">
                        #{grpIdx + 1}
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-gray-900">Grupo de {group.files.length} archivos idénticos</h4>
                        <div className="flex items-center gap-3 text-xs text-gray-500 font-mono mt-0.5">
                          <span>Criterio: {group.criteria === 'md5' ? 'Hash MD5 Idéntico' : 'Nombre y Tamaño'}</span>
                          {originalFile?.size && <span>• Tamaño: {formatFileSize(originalFile.size)}</span>}
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedDuplicatesToDelete((prev) => {
                            const next = new Set(prev);
                            const allGroupSelected = cloneFiles.every((c) => next.has(c.id));
                            if (allGroupSelected) {
                              cloneFiles.forEach((c) => next.delete(c.id));
                            } else {
                              cloneFiles.forEach((c) => next.add(c.id));
                            }
                            return next;
                          });
                        }}
                        className="text-xs text-blue-600 hover:text-blue-800 font-medium px-2.5 py-1 bg-blue-50 hover:bg-blue-100 rounded-md transition-colors"
                      >
                        {cloneFiles.every((c) => selectedDuplicatesToDelete.has(c.id))
                          ? 'Deseleccionar grupo'
                          : 'Seleccionar todos los clones'}
                      </button>
                    </div>
                  </div>

                  {/* Lista comparativa del Grupo */}
                  <div className="p-5 space-y-3">
                    {/* Archivo Original */}
                    {originalFile && (
                      <div className="p-4 rounded-xl bg-emerald-50/70 border-2 border-emerald-300 text-emerald-950 flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <div className="flex items-start md:items-center gap-3 min-w-0 flex-1">
                          <button
                            onClick={() => togglePlay(originalFile as any)}
                            className={`w-9 h-9 rounded-full flex items-center justify-center transition-all shrink-0 ${
                              playingFileId === originalFile.id && isPlaying
                                ? 'bg-emerald-700 text-white shadow-md ring-2 ring-emerald-400'
                                : 'bg-emerald-100 hover:bg-emerald-200 text-emerald-800'
                            }`}
                            title={playingFileId === originalFile.id && isPlaying ? 'Pausar' : 'Reproducir original'}
                          >
                            {playingFileId === originalFile.id && isPlaying ? (
                              <Pause className="w-4 h-4" />
                            ) : (
                              <Play className="w-4 h-4 ml-0.5" />
                            )}
                          </button>

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <Badge variant="green" className="text-xs bg-emerald-600 text-white font-bold px-2 py-0.5">
                                ORIGINAL (MANTENER)
                              </Badge>
                              <span className="text-xs text-emerald-800 font-medium">Subido primero</span>
                            </div>
                            <p className="font-semibold text-sm text-emerald-950 truncate mt-1" title={originalFile.name}>
                              {originalFile.name}
                            </p>
                            <div className="text-[11px] text-emerald-700/90 font-mono flex items-center gap-3 mt-0.5">
                              <span>ID: {originalFile.id}</span>
                              {originalFile.createdTime && <span>• Creado: {new Date(originalFile.createdTime).toLocaleString()}</span>}
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center justify-end gap-2 shrink-0">
                          {originalFile.webViewLink && (
                            <a
                              href={originalFile.webViewLink}
                              target="_blank"
                              rel="noreferrer"
                              className="p-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 rounded-lg transition-colors"
                              title="Abrir en Drive"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}
                          <button
                            onClick={() => handleOpenRename(originalFile as any)}
                            className="p-2 text-emerald-700 hover:text-emerald-900 hover:bg-emerald-100 rounded-lg transition-colors"
                            title="Editar nombre"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Clones / Duplicados */}
                    <div className="space-y-2 pl-0 md:pl-4 border-l-2 border-amber-200 mt-2">
                      <div className="text-xs font-semibold text-amber-800 uppercase tracking-wider mb-1 flex items-center gap-1.5">
                        <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
                        Copias Duplicadas a Eliminar ({cloneFiles.length}):
                      </div>

                      {cloneFiles.map((clone) => {
                        const isSelected = selectedDuplicatesToDelete.has(clone.id);

                        return (
                          <div
                            key={clone.id}
                            className={`p-3.5 rounded-xl border text-xs flex flex-col md:flex-row md:items-center justify-between gap-3 transition-colors ${
                              isSelected
                                ? 'bg-red-50/70 border-red-300 text-red-950'
                                : 'bg-amber-50/30 border-amber-200 hover:bg-amber-50 text-gray-800'
                            }`}
                          >
                            <div className="flex items-start md:items-center gap-3 min-w-0 flex-1">
                              <input
                                type="checkbox"
                                checked={isSelected}
                                onChange={() => toggleSelectDuplicate(clone.id)}
                                className="mt-1 md:mt-0 rounded text-red-600 focus:ring-red-500 w-4 h-4 cursor-pointer"
                              />

                              <button
                                onClick={() => togglePlay(clone as any)}
                                className={`w-8 h-8 rounded-full flex items-center justify-center transition-all shrink-0 ${
                                  playingFileId === clone.id && isPlaying
                                    ? 'bg-amber-700 text-white shadow-md ring-2 ring-amber-400'
                                    : 'bg-amber-100 hover:bg-amber-200 text-amber-800'
                                }`}
                                title={playingFileId === clone.id && isPlaying ? 'Pausar' : 'Reproducir copia'}
                              >
                                {playingFileId === clone.id && isPlaying ? (
                                  <Pause className="w-3.5 h-3.5" />
                                ) : (
                                  <Play className="w-3.5 h-3.5 ml-0.5" />
                                )}
                              </button>

                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2">
                                  <Badge variant="warning" className="text-xs bg-amber-100 text-amber-800">
                                    CLON DUPLICADO
                                  </Badge>
                                  {isSelected && <span className="text-[11px] font-bold text-red-600">Marcado para eliminación</span>}
                                </div>
                                <p className="font-medium text-xs text-gray-900 truncate mt-0.5" title={clone.name}>
                                  {clone.name}
                                </p>
                                <div className="text-[11px] text-gray-400 font-mono flex items-center gap-3 mt-0.5">
                                  <span>ID: {clone.id}</span>
                                  {clone.createdTime && <span>• Creado: {new Date(clone.createdTime).toLocaleString()}</span>}
                                </div>
                              </div>
                            </div>

                            <div className="flex items-center justify-end gap-1.5 shrink-0">
                              {clone.webViewLink && (
                                <a
                                  href={clone.webViewLink}
                                  target="_blank"
                                  rel="noreferrer"
                                  className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                                  title="Abrir en Drive"
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </a>
                              )}

                              <button
                                onClick={() => setFileToDelete(clone as any)}
                                className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                title="Eliminar este duplicado"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : audioFiles.length > 0 ? (
        /* VISTA LISTADO GENERAL DE AUDIOS */
        <div className="bg-white rounded-xl border border-gray-200 shadow-xs overflow-hidden">
          {audioFilesFiltrados.length === 0 && (
            <div className="p-4 text-sm text-gray-500 text-center">Ningún audio coincide con la asesoría seleccionada.</div>
          )}
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm text-gray-600">
              <thead className="bg-gray-50 border-b border-gray-200 text-xs font-semibold text-gray-500 uppercase tracking-wider">
                <tr>
                  <th className="py-3.5 px-4 w-12 text-center">Audio</th>
                  <th className="py-3.5 px-4">Nombre en Drive</th>
                  <th className="py-3.5 px-4">Asesoría</th>
                  <th className="py-3.5 px-4">Sugerencia Normalizada</th>
                  <th className="py-3.5 px-4">Tamaño</th>
                  <th className="py-3.5 px-4">Estado</th>
                  <th className="py-3.5 px-4 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {audioFilesFiltrados.map((file) => {
                  const isCurPlaying = playingFileId === file.id && isPlaying;
                  const isDuplicate = duplicateFileIds.has(file.id);
                  const isNormalized = file.name === file.suggestedName;

                  return (
                    <tr key={file.id} className={`hover:bg-gray-50/80 transition-colors ${isDuplicate ? 'bg-amber-50/40' : ''}`}>
                      {/* Botón de Play/Pause */}
                      <td className="py-3 px-4 text-center">
                        <button
                          onClick={() => togglePlay(file)}
                          className={`w-8 h-8 rounded-full flex items-center justify-center transition-colors ${
                            isCurPlaying
                              ? 'bg-blue-600 text-white shadow-md'
                              : 'bg-gray-100 hover:bg-blue-100 text-gray-700 hover:text-blue-600'
                          }`}
                          title={isCurPlaying ? 'Pausar audio' : 'Reproducir audio'}
                        >
                          {isCurPlaying ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4 ml-0.5" />}
                        </button>
                      </td>

                      {/* Nombre actual */}
                      <td className="py-3 px-4 max-w-xs md:max-w-md">
                        <div className="font-medium text-gray-900 truncate" title={file.name}>
                          {file.name}
                        </div>
                        <div className="text-xs text-gray-400 mt-0.5 font-mono">ID: {file.id}</div>
                      </td>

                      {/* Asesoría (cruzada con Audio.asesoriaId por fileId) */}
                      <td className="py-3 px-4">
                        {file.asesoriaLabel ? (
                          <Badge className="bg-blue-50 text-blue-700 border border-blue-200 whitespace-nowrap">{file.asesoriaLabel}</Badge>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Sin identificar</span>
                        )}
                      </td>

                      {/* Sugerencia normalizada */}
                      <td className="py-3 px-4 max-w-xs md:max-w-md">
                        {file.suggestedName ? (
                          <div className="flex items-center gap-2">
                            <span className="font-mono text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-1 rounded-md border border-emerald-200 truncate">
                              {file.suggestedName}
                            </span>
                            {!isNormalized && (
                              <button
                                onClick={() => renombrarArchivo(file.id, file.suggestedName!)}
                                disabled={isProcessing}
                                title="Aplicar nombre sugerido"
                                className="p-1 text-emerald-600 hover:text-emerald-800 hover:bg-emerald-100 rounded-md transition-colors"
                              >
                                <Check className="w-3.5 h-3.5" />
                              </button>
                            )}
                          </div>
                        ) : (
                          <span className="text-xs text-gray-400 italic">Sin sugerencia</span>
                        )}
                      </td>

                      {/* Tamaño */}
                      <td className="py-3 px-4 whitespace-nowrap text-xs text-gray-500 font-mono">{formatFileSize(file.size)}</td>

                      {/* Estado */}
                      <td className="py-3 px-4 whitespace-nowrap">
                        {isDuplicate ? (
                          <Badge variant="warning" className="text-xs bg-amber-100 text-amber-800 border-amber-300">
                            Duplicado
                          </Badge>
                        ) : isNormalized ? (
                          <Badge variant="green" className="text-xs bg-emerald-100 text-emerald-800 border-emerald-300">
                            Normalizado
                          </Badge>
                        ) : (
                          <Badge variant="gray" className="text-xs bg-gray-100 text-gray-600">
                            Pendiente
                          </Badge>
                        )}
                      </td>

                      {/* Acciones */}
                      <td className="py-3 px-4 text-right whitespace-nowrap">
                        <div className="flex items-center justify-end gap-1.5">
                          <button
                            onClick={() => handleOpenRename(file)}
                            className="p-1.5 text-gray-500 hover:text-(--admin-blue) hover:bg-blue-50 rounded-lg transition-colors"
                            title="Editar nombre"
                          >
                            <Edit3 className="w-4 h-4" />
                          </button>

                          {file.webViewLink && (
                            <a
                              href={file.webViewLink}
                              target="_blank"
                              rel="noreferrer"
                              className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                              title="Abrir en Google Drive"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          )}

                          <button
                            onClick={() => setFileToDelete(file)}
                            className="p-1.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                            title="Eliminar archivo"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      ) : folderId ? (
        <Card className="p-8 text-center bg-gray-50 border border-gray-200">
          <FileAudio className="w-12 h-12 mx-auto text-gray-400 mb-3" />
          <h3 className="text-lg font-semibold text-gray-800">No se encontraron audios en esta carpeta</h3>
          <p className="text-sm text-gray-500 mt-1">Verifica el ID de la carpeta o los permisos en Google Drive.</p>
        </Card>
      ) : null}

      {/* Modal: Renombrar Individual */}
      {fileToRename && (
        <Modal isOpen={true} onClose={() => setFileToRename(null)} title="Renombrar Archivo en Google Drive">
          <div className="space-y-4">
            <div>
              <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Nombre Actual</label>
              <p className="text-sm text-gray-700 bg-gray-50 p-2.5 rounded-md font-mono break-all border border-gray-200">
                {fileToRename.name}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nuevo Nombre <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                value={customNewName}
                onChange={(e) => setCustomNewName(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-(--admin-blue) focus:outline-hidden text-sm"
                placeholder="20260423 - 5.1 - 5.2 - 5.3.mp3"
              />
              <p className="text-xs text-gray-500 mt-1">
                Formato estándar sugerido: <span className="font-mono">YYYYMMDD - [Norma/Texto &lt;= 15 chars][(n)].mp3</span>
              </p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
              <Button variant="outline" onClick={() => setFileToRename(null)} disabled={isProcessing}>
                Cancelar
              </Button>
              <Button
                onClick={handleConfirmRename}
                disabled={isProcessing || !customNewName.trim()}
                className="bg-(--admin-blue) hover:bg-blue-700 text-white"
              >
                {isProcessing ? 'Guardando...' : 'Guardar Nombre'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Eliminar Individual */}
      {fileToDelete && (
        <Modal isOpen={true} onClose={() => setFileToDelete(null)} title="Confirmar Eliminación en Google Drive">
          <div className="space-y-4">
            <div className="p-3 bg-red-50 border border-red-200 text-red-700 rounded-lg flex items-start gap-3">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5" />
              <p className="text-sm">
                Esta acción manda el archivo a la papelera de Google Drive (reversible) — no lo borra de forma permanente.
              </p>
            </div>

            <div>
              <span className="text-xs font-semibold text-gray-500 uppercase">Archivo a eliminar:</span>
              <p className="text-sm font-medium text-gray-900 mt-1 font-mono break-all">{fileToDelete.name}</p>
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
              <Button variant="outline" onClick={() => setFileToDelete(null)} disabled={isProcessing}>
                Cancelar
              </Button>
              <Button onClick={handleConfirmDeleteSingle} disabled={isProcessing} className="bg-red-600 hover:bg-red-700 text-white">
                {isProcessing ? 'Eliminando...' : 'Eliminar de Drive'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Previsualización de Auto-Normalización */}
      {showNormalizeModal && (
        <Modal isOpen={true} onClose={() => setShowNormalizeModal(false)} title="Auto-Normalizar Nombres de Audios en Lote">
          <div className="space-y-4 max-h-[70vh] flex flex-col">
            <p className="text-sm text-gray-600">A continuación se muestra el plan de renombrado automático calculado para esta carpeta:</p>

            <div className="overflow-y-auto border border-gray-200 rounded-lg divide-y divide-gray-100 flex-1">
              {normalizePlan.map((item, idx) => (
                <div key={item.fileId || idx} className="p-3 text-xs flex flex-col md:flex-row md:items-center justify-between gap-2">
                  <div className="flex-1 truncate">
                    <span className="text-gray-500 block truncate" title={item.currentName}>
                      {item.currentName}
                    </span>
                    <span className="font-semibold text-emerald-700 block truncate font-mono mt-0.5">→ {item.targetName}</span>
                  </div>
                  <div>
                    {item.currentName === item.targetName ? (
                      <Badge variant="gray" className="text-xs bg-gray-100 text-gray-500">
                        Sin cambios
                      </Badge>
                    ) : (
                      <Badge variant="green" className="text-xs bg-emerald-100 text-emerald-800">
                        Se renombrará
                      </Badge>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex justify-end gap-2 pt-4 border-t border-gray-100">
              <Button variant="outline" onClick={() => setShowNormalizeModal(false)} disabled={isProcessing}>
                Cancelar
              </Button>
              <Button
                onClick={handleApplyNormalize}
                disabled={isProcessing}
                className="bg-emerald-600 hover:bg-emerald-700 text-white flex items-center gap-1.5"
              >
                <Sparkles className="w-4 h-4" />
                {isProcessing ? 'Aplicando...' : 'Confirmar y Aplicar a Todos'}
              </Button>
            </div>
          </div>
        </Modal>
      )}

      {/* Modal: Detección y Eliminación de Duplicados */}
      {showDuplicatesModal && (
        <Modal isOpen={true} onClose={() => setShowDuplicatesModal(false)} title="Archivos Duplicados Detectados en Drive">
          <div className="space-y-4 max-h-[70vh] flex flex-col">
            <div className="p-3 bg-amber-50 border border-amber-200 text-amber-800 rounded-lg flex items-start gap-2.5 text-sm">
              <AlertTriangle className="w-5 h-5 shrink-0 mt-0.5 text-amber-600" />
              <div>
                <p className="font-medium">Selecciona los duplicados que deseas eliminar:</p>
                <p className="text-xs mt-0.5">
                  El archivo marcado como <strong className="text-emerald-700">ORIGINAL</strong> es el más antiguo y se mantendrá a salvo.
                </p>
              </div>
            </div>

            <div className="overflow-y-auto space-y-3 flex-1">
              {duplicateGroups.map((group, grpIdx) => (
                <div key={group.key || grpIdx} className="border border-gray-200 rounded-xl p-3 bg-gray-50">
                  <div className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 flex items-center justify-between">
                    <span>
                      Grupo {grpIdx + 1} ({group.criteria === 'md5' ? 'Hash MD5 Idéntico' : 'Mismo Nombre y Tamaño'})
                    </span>
                    <span>{group.files.length} archivos</span>
                  </div>

                  <div className="space-y-1.5">
                    {group.files.map((file) => (
                      <div
                        key={file.id}
                        className={`p-2.5 rounded-lg border text-xs flex items-center justify-between gap-3 ${
                          file.isOriginal
                            ? 'bg-emerald-50/70 border-emerald-200 text-emerald-900'
                            : 'bg-white border-gray-200 text-gray-700'
                        }`}
                      >
                        <div className="flex items-center gap-2.5 min-w-0 flex-1">
                          {!file.isOriginal ? (
                            <input
                              type="checkbox"
                              checked={selectedDuplicatesToDelete.has(file.id)}
                              onChange={() => toggleSelectDuplicate(file.id)}
                              className="rounded text-red-600 focus:ring-red-500 w-4 h-4"
                            />
                          ) : (
                            <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                          )}

                          <div className="min-w-0 flex-1">
                            <p className="font-medium truncate" title={file.name}>
                              {file.name}
                            </p>
                            <div className="text-gray-400 font-mono text-[11px] flex items-center gap-2 mt-0.5">
                              <span>ID: {file.id}</span>
                              {file.size && <span>• {formatFileSize(file.size)}</span>}
                              {file.createdTime && <span>• {new Date(file.createdTime).toLocaleDateString()}</span>}
                            </div>
                          </div>
                        </div>

                        <div>
                          {file.isOriginal ? (
                            <Badge variant="green" className="text-xs bg-emerald-100 text-emerald-800">
                              Original
                            </Badge>
                          ) : (
                            <Badge variant="warning" className="text-xs bg-amber-100 text-amber-800">
                              Duplicado
                            </Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between pt-4 border-t border-gray-100">
              <span className="text-xs text-gray-500">
                Seleccionados para eliminar: <strong>{selectedDuplicatesToDelete.size}</strong>
              </span>

              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowDuplicatesModal(false)} disabled={isProcessing}>
                  Cancelar
                </Button>
                <Button
                  onClick={handleConfirmDeleteDuplicates}
                  disabled={isProcessing || selectedDuplicatesToDelete.size === 0}
                  className="bg-red-600 hover:bg-red-700 text-white flex items-center gap-1.5"
                >
                  <Trash2 className="w-4 h-4" />
                  {isProcessing ? 'Eliminando...' : `Eliminar Seleccionados (${selectedDuplicatesToDelete.size})`}
                </Button>
              </div>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
